import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { DialogBody, DialogFooter } from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../../web/components-v2/Select';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { useSkillAreas } from '../../../queries/Scenarios';
import type { CreateScenarioInput, Scenario, UpdateScenarioInput } from '../../../voice-agent/types/scenarios';
import { styleSheet } from './styles';

interface IProps {
	scenario?: Scenario | null;
	/** Default skill area ID for creating a new scenario under a specific skill area */
	defaultSkillAreaId?: string | null;
	onSubmit: (input: CreateScenarioInput | UpdateScenarioInput) => Promise<void>;
	onCancel: () => void;
	isSubmitting: boolean;
}

export const ScenarioForm: React.FC<IProps> = ({ scenario, defaultSkillAreaId, onSubmit, onCancel, isSubmitting }) => {
	const [name, setName] = React.useState(scenario?.name || '');
	const [description, setDescription] = React.useState(scenario?.description || '');
	const [skillAreaId, setSkillAreaId] = React.useState(scenario?.skillAreaId || defaultSkillAreaId || '');
	const [prompt, setPrompt] = React.useState(scenario?.prompt || '');
	const [active, setActive] = React.useState(scenario?.active ?? true);
	const [errors, setErrors] = React.useState<{ name?: string; skillAreaId?: string; prompt?: string }>({});

	const { data: skillAreasData, isPending: isLoadingSkillAreas } = useSkillAreas();
	// Hooks now return arrays directly
	const skillAreas = skillAreasData || [];
	const selectedSkillArea = skillAreas.find(sa => sa.id === skillAreaId);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validate required fields
		const newErrors: { name?: string; skillAreaId?: string; prompt?: string } = {};

		if (!name.trim()) {
			newErrors.name = 'Name is required';
		}
		if (!skillAreaId) {
			newErrors.skillAreaId = 'Skill Area is required';
		}
		if (!prompt.trim()) {
			newErrors.prompt = 'Prompt is required';
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		setErrors({}); // Clear errors on valid submit

		const input: CreateScenarioInput | UpdateScenarioInput = {
			name: name.trim(),
			description: description.trim(),
			skillAreaId,
			prompt: prompt.trim(),
			active,
		};

		await onSubmit(input);
	};

	const handleSkillAreaChange = (value: string) => {
		setSkillAreaId(value);
		if (errors.skillAreaId) {
			setErrors(prev => ({ ...prev, skillAreaId: undefined }));
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<DialogBody>
				<div className={css(styleSheet.formGroup)}>
					<label htmlFor='name' className={css(styleSheet.label)}>
						Name <span className={css(styleSheet.required)}>*</span>
					</label>
					<Input
						type='text'
						id='name'
						value={name}
						onChange={e => {
							setName(e.target.value);
							if (errors.name) {
								setErrors(prev => ({ ...prev, name: undefined }));
							}
						}}
						placeholder='Enter scenario name'
						required
						disabled={isSubmitting}
					/>
					{errors.name && <span className={css(styleSheet.error)}>{errors.name}</span>}
				</div>

				<div className={css(styleSheet.formGroup)}>
					<label htmlFor='description' className={css(styleSheet.label)}>
						Description
					</label>
					<TextArea
						id='description'
						value={description}
						onChange={e => setDescription(e.target.value)}
						placeholder='Enter scenario description (optional)'
						rows={3}
						disabled={isSubmitting}
					/>
				</div>

				<div className={css(styleSheet.formGroup)}>
					<label htmlFor='skillArea' className={css(styleSheet.label)}>
						Skill Area <span className={css(styleSheet.required)}>*</span>
					</label>
					{isLoadingSkillAreas ? (
						<div style={{ padding: '8px 0' }}>
							<LoadingSpinner />
						</div>
					) : (
						<Select value={skillAreaId} onValueChange={handleSkillAreaChange} disabled={isSubmitting}>
							<SelectTrigger>{selectedSkillArea ? selectedSkillArea.name : 'Select a skill area'}</SelectTrigger>
							<SelectContent>
								{skillAreas.map(sa => (
									<SelectItem key={sa.id} value={sa.id}>
										{sa.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
					{errors.skillAreaId && <span className={css(styleSheet.error)}>{errors.skillAreaId}</span>}
				</div>

				<div className={css(styleSheet.formGroup)}>
					<label htmlFor='prompt' className={css(styleSheet.label)}>
						Prompt <span className={css(styleSheet.required)}>*</span>
					</label>
					<TextArea
						id='prompt'
						value={prompt}
						onChange={e => {
							setPrompt(e.target.value);
							if (errors.prompt) {
								setErrors(prev => ({ ...prev, prompt: undefined }));
							}
						}}
						placeholder='Enter AI scenario instructions/prompt'
						rows={6}
						required
						disabled={isSubmitting}
					/>
					{errors.prompt && <span className={css(styleSheet.error)}>{errors.prompt}</span>}
				</div>

				<div className={css(styleSheet.formGroup)}>
					<label className={css(styleSheet.checkboxLabel)}>
						<Checkbox
							checked={active}
							onCheckedChange={checked => setActive(checked === true)}
							disabled={isSubmitting}
						/>
						<span className={css(styleSheet.checkboxText)}>Active</span>
					</label>
				</div>
			</DialogBody>

			<DialogFooter>
				<Button type='button' variant='secondary' onClick={onCancel} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button
					type='submit'
					variant='primary'
					disabled={isSubmitting || !name.trim() || !skillAreaId || !prompt.trim()}
				>
					{isSubmitting ? 'Saving...' : scenario ? 'Update' : 'Create'}
				</Button>
			</DialogFooter>
		</form>
	);
};
